/**
 * Tournament Chat real-time – Ably.
 * Subscribes to tournament_chat:${conversationId} and user:${userId}.
 * Typing via REST: POST .../tournaments/:tournamentId/chat/conversations/:conversationId/typing.
 * See docs/ABLY_CUSTOMER_PORTAL.md.
 */
import { subscribeToChannel } from './ablyRealtime';
import { getUserIdFromToken } from '@/utils/jwt';
import { apiService } from './api';

const TOURNAMENT_CHAT_EVENTS = [
  'tournament_chat:message:new',
  'tournament_chat:message:updated',
  'tournament_chat:message:deleted',
  'tournament_chat:post:new',
  'tournament_chat:post:updated',
  'tournament_chat:post:deleted',
  'tournament_chat:comment:new',
  'tournament_chat:comment:updated',
  'tournament_chat:comment:deleted',
  'tournament_chat:reaction:new',
  'tournament_chat:reaction:removed',
  'tournament_chat:typing',
] as const;

const USER_TOURNAMENT_EVENTS = ['tournament_chat:notification'] as const;

export class TournamentChatWebSocketService {
  private eventHandlers: Map<string, Set<Function>> = new Map();
  private unsubChat: (() => void) | null = null;
  private unsubUser: (() => void) | null = null;
  private currentConversationId: string | null = null;
  private currentTournamentId: string | null = null;
  private userId: string | null = null;
  private lastMessageId: string | null = null;
  private lastMessageTime = 0;

  connect(conversationId?: string, tournamentId?: string): void {
    if (tournamentId) this.currentTournamentId = tournamentId;

    const { accessToken } = apiService.getTokens();
    if (!accessToken) {
      console.warn('[TournamentChatWebSocket] No access token');
      return;
    }

    const uid = getUserIdFromToken(accessToken);
    if (!uid) {
      console.warn('[TournamentChatWebSocket] Could not extract userId');
      return;
    }

    this.userId = uid;

    const emit = (event: string, data: any) => {
      this.eventHandlers.get(event)?.forEach((h) => {
        try {
          h(data);
        } catch (e) {
          console.error('[TournamentChatWebSocket] handler error', e);
        }
      });
    };

    if (!this.unsubUser) {
      this.unsubUser = subscribeToChannel(
        `user:${uid}`,
        USER_TOURNAMENT_EVENTS as unknown as readonly string[],
        emit
      );
    }

    if (conversationId) {
      this.joinConversation(conversationId);
    }
  }

  joinConversation(conversationId: string): void {
    if (this.currentConversationId === conversationId && this.unsubChat) return;
    if (this.unsubChat) {
      this.unsubChat();
      this.unsubChat = null;
    }

    this.currentConversationId = conversationId;

    const emit = (event: string, data: any) => {
      this.eventHandlers.get(event)?.forEach((h) => {
        try {
          h(data);
        } catch (e) {
          console.error('[TournamentChatWebSocket] handler error', e);
        }
      });
    };

    const processMessage = (data: any) => {
      const message = data?.message;
      const messageId = message?.id ?? data?.messageId;
      const now = Date.now();
      if (messageId && messageId === this.lastMessageId && now - this.lastMessageTime < 500) return;
      this.lastMessageId = messageId ?? null;
      this.lastMessageTime = now;
      emit('tournament_chat:message', data);
    };

    this.unsubChat = subscribeToChannel(
      `tournament_chat:${conversationId}`,
      TOURNAMENT_CHAT_EVENTS as unknown as readonly string[],
      (event, data) => {
        if (event === 'tournament_chat:message:new') processMessage(data);
        else emit(event, data);
      }
    );
  }

  leaveConversation(conversationId: string): void {
    if (this.currentConversationId === conversationId && this.unsubChat) {
      this.unsubChat();
      this.unsubChat = null;
      this.currentConversationId = null;
    }
  }

  startTyping(conversationId: string): void {
    const tid = this.currentTournamentId;
    if (tid) apiService.sendTournamentChatTyping(tid, conversationId, true).catch(() => {});
  }

  stopTyping(conversationId: string): void {
    const tid = this.currentTournamentId;
    if (tid) apiService.sendTournamentChatTyping(tid, conversationId, false).catch(() => {});
  }

  on(event: string, handler: Function): void {
    if (!this.eventHandlers.has(event)) this.eventHandlers.set(event, new Set());
    this.eventHandlers.get(event)!.add(handler);
  }

  off(event: string, handler: Function): void {
    this.eventHandlers.get(event)?.delete(handler);
  }

  isConnected(): boolean {
    return this.unsubUser != null || this.unsubChat != null;
  }

  disconnect(): void {
    if (this.unsubChat) {
      this.unsubChat();
      this.unsubChat = null;
    }
    if (this.unsubUser) {
      this.unsubUser();
      this.unsubUser = null;
    }
    this.currentConversationId = null;
    this.currentTournamentId = null;
    this.userId = null;
    this.eventHandlers.clear();
  }
}

export const tournamentChatWebSocketService = new TournamentChatWebSocketService();
